
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { devLog, devError } from "@/utils/devLog";

/**
 * Calculate the progress percentage for a specific course and user
 */
export const calculateCourseProgress = async (courseId: string, userId: string): Promise<number> => {
  try {
    // Get total lessons count
    const { data: modules } = await supabase
      .from('modules')
      .select('id')
      .eq('course_id', courseId);

    if (!modules || modules.length === 0) return 0;
    
    const moduleIds = modules.map(m => m.id);
    
    const { data: lessons, error: lessonsError } = await supabase
      .from('lessons')
      .select('id', { count: 'exact' })
      .in('module_id', moduleIds);
      
    if (lessonsError) {
      devError('Error fetching lessons:', lessonsError);
      return 0;
    }
    
    const totalLessons = lessons?.length || 0;
    
    if (totalLessons === 0) return 0;
    
    // Get completed lessons count
    const { data: completions, error: completionsError } = await supabase
      .from('lesson_completions')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .eq('course_id', courseId);
      
    if (completionsError) {
      devError('Error fetching completions:', completionsError);
      return 0;
    }
    
    const completedLessons = completions?.length || 0;
    
    // Calculate percentage
    return Math.round((completedLessons / totalLessons) * 100);
  } catch (error) {
    devError('Error calculating course progress:', error);
    return 0;
  }
};

export const useLessonCompletions = (courseId: string) => {
  const queryClient = useQueryClient();
  const [completedLessonIds, setCompletedLessonIds] = useState<Set<string>>(new Set());
  const [totalLessons, setTotalLessons] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch total lessons for this course
  useEffect(() => {
    const fetchTotalLessons = async () => {
      if (!courseId) return;
      
      try {
        // Get all modules for this course
        const { data: modules } = await supabase
          .from('modules')
          .select('id')
          .eq('course_id', courseId);

        if (!modules || modules.length === 0) {
          setTotalLessons(0);
          return;
        }
        
        const moduleIds = modules.map(m => m.id);
        
        // Get count of all lessons in these modules
        const { data: lessons, error } = await supabase
          .from('lessons')
          .select('id', { count: 'exact' })
          .in('module_id', moduleIds);
          
        if (error) {
          devError('Error fetching lessons count:', error);
          setTotalLessons(0);
          return;
        }
        
        setTotalLessons(lessons?.length || 0);
      } catch (error) {
        devError('Error calculating total lessons:', error);
        setTotalLessons(0);
      }
    };
    
    fetchTotalLessons();
  }, [courseId]);

  const { data: completions } = useQuery({
    queryKey: ['lesson-completions', courseId],
    queryFn: async () => {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('lesson_completions')
        .select('lesson_id')
        .eq('user_id', user.id)
        .eq('course_id', courseId);

      if (error) {
        devError('Error fetching lesson completions:', error);
        return [];
      }
      setIsLoading(false);
      return data || [];
    },
    enabled: !!courseId,
  });

  useEffect(() => {
    if (completions) {
      const completedIds = new Set(completions.map(c => c.lesson_id));
      setCompletedLessonIds(completedIds);
    }
  }, [completions]);

  const isLessonCompleted = (lessonId: string) => completedLessonIds.has(lessonId);

  const calculateProgress = () => {
    if (!completions || totalLessons === 0) return 0;
    return Math.min(100, Math.round((completions.length / totalLessons) * 100));
  };

  const markLessonCompleted = useMutation({
    mutationFn: async ({ lessonId }: { lessonId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Must be logged in to mark lessons as complete");

      // Check if already completed
      const { data: existing, error: checkError } = await supabase
        .from('lesson_completions')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .eq('lesson_id', lessonId)
        .limit(1);

      if (checkError) {
        devError('Error checking lesson completion:', checkError);
        throw checkError;
      }
        
      if (existing && existing.length > 0) {
        devLog('Lesson already marked as complete:', { lessonId });
        return existing[0];
      }

      const { data, error } = await supabase
        .from('lesson_completions')
        .insert({
          user_id: user.id,
          course_id: courseId,
          lesson_id: lessonId,
        })
        .select()
        .single();

      if (error) {
        devError('Error marking lesson as complete:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, { lessonId }) => {
      setCompletedLessonIds(prev => new Set([...prev, lessonId]));
      queryClient.invalidateQueries({ queryKey: ['lesson-completions', courseId] });
      toast.success('Lesson marked as complete');
    },
    onError: (error) => {
      devError('Failed to mark lesson as complete:', error);
      toast.error('Failed to mark lesson as complete');
    },
  });

  const markLessonIncomplete = useMutation({
    mutationFn: async ({ lessonId }: { lessonId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Must be logged in to mark lessons as incomplete");

      const { error } = await supabase
        .from('lesson_completions')
        .delete()
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .eq('lesson_id', lessonId);

      if (error) {
        devError('Error marking lesson as incomplete:', error);
        throw error;
      }
      
      return { success: true };
    },
    onSuccess: (_, { lessonId }) => {
      setCompletedLessonIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(lessonId);
        return newSet;
      });
      queryClient.invalidateQueries({ queryKey: ['lesson-completions', courseId] });
      toast.success('Lesson marked as incomplete');
    },
    onError: (error) => {
      devError('Failed to mark lesson as incomplete:', error);
      toast.error('Failed to mark lesson as incomplete');
    },
  });

  // Add toggleCompletion for legacy support - ensures it returns a Promise
  const toggleCompletion = useMutation({
    mutationFn: async ({ lessonId, courseId, isCompleted }: { lessonId: string, courseId: string, isCompleted: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Must be logged in to toggle lesson completion");

      if (isCompleted) {
        // Mark as complete
        const { data: existing } = await supabase
          .from('lesson_completions')
          .select('id', { count: 'exact' })
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .eq('lesson_id', lessonId)
          .limit(1);
          
        if (existing && existing.length > 0) {
          // Already exists, which is fine
          return { action: 'completed', success: true };
        }
        
        const { data, error } = await supabase
          .from('lesson_completions')
          .insert({
            user_id: user.id,
            course_id: courseId,
            lesson_id: lessonId,
          })
          .select()
          .single();

        if (error) {
          if (error.code === '23505') {
            // Already exists, which is fine
            return { action: 'completed', success: true };
          }
          throw error;
        }

        return { action: 'completed', success: true };
      } else {
        // Mark as incomplete
        const { error } = await supabase
          .from('lesson_completions')
          .delete()
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .eq('lesson_id', lessonId);

        if (error) throw error;
        return { action: 'incomplete', success: true };
      }
    },
    onSuccess: (result, { lessonId, isCompleted }) => {
      if (isCompleted) {
        setCompletedLessonIds(prev => new Set([...prev, lessonId]));
      } else {
        setCompletedLessonIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(lessonId);
          return newSet;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['lesson-completions', courseId] });
    },
    onError: (error) => {
      devError('Failed to toggle lesson completion:', error);
      toast.error('Failed to update lesson status');
    }
  });

  return {
    completions: completions || [],
    completedLessonIds,
    isLessonCompleted,
    markLessonCompleted,
    markLessonIncomplete,
    calculateProgress,
    toggleCompletion,
    totalLessons,
    isLoading
  };
};
